import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { concatMap, delay, retryWhen, skipWhile, takeUntil, tap } from 'rxjs/operators';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';

import { DOOD_API } from '@config/ws.config';
import { DestroyerBase } from '@core/base/destroyer/destroyer.base';
import { AuthStoreSelector } from '@common/selectors/authentication.selector';
import { MarketplaceService } from '@core/services/marketplace/marketplace.service';
import { NoConnectionModalService } from '@core/services/no-connection-modal/no-connection-modal.service';
import { AnalyticsService } from '@core/services/app/analytics.service';

export const retryCount = 20;
export const baseRetryDelaySeconds = 2;
export const maxRetryDelaySeconds = 64;

@Injectable()
export class DoodApiInterceptor extends DestroyerBase implements HttpInterceptor {
  private language!: string;
  private firebaseToken: string | null = null;

  /**
   * Calculate exponential backoff delay with maximum cap
   * @param attemptNumber - Current retry attempt (0-based)
   * @returns Delay in milliseconds
   */
  private calculateExponentialDelay(attemptNumber: number): number {
    const delaySeconds = Math.min(
      baseRetryDelaySeconds * Math.pow(2, attemptNumber),
      maxRetryDelaySeconds,
    );
    return delaySeconds * 1000; // Convert to milliseconds
  }

  constructor(
    private authSelector: AuthStoreSelector,
    private readonly translationService: TranslateService,
    private readonly marketplaceService: MarketplaceService,
    private readonly noConnectionModalService: NoConnectionModalService,
    private readonly analyticsService: AnalyticsService,
  ) {
    super();
    this.getFirebaseToken();

    this.translationService.onLangChange
      .pipe(takeUntil(this._destroyerRef))
      .subscribe(v => (this.language = v.lang));
  }

  intercept(
    request: HttpRequest<Record<string, unknown>>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    // Epson ePOS printer API doesn't allow custom header
    if (request.url.includes('cgi-bin/epos/service.cgi')) {
      return next.handle(request);
    }

    const analyticsSessionId = this.analyticsService.getAnalyticsSessionId();
    const analyticsAnonymousId = this.analyticsService.getAnalyticsAnonymousId();

    request = request.clone({
      setHeaders: {
        'Firebase-tenant': this.marketplaceService?.getMarketplaceTenantId() ?? 'DEFAULT-TENANT',

        'X-Analytics-Session-Id': analyticsSessionId ?? '',
        'X-Analytics-Anonymous-Id': analyticsAnonymousId ?? '',
      },
    });

    if (request.method === 'PATCH') {
      request = request.clone({
        setHeaders: {
          'Content-Type': 'application/merge-patch+json',
        },
      });
    }

    if (request.url.startsWith(DOOD_API)) {
      request = request.clone({
        setHeaders: {
          Authorization: `${this.firebaseToken}`,
          'X-locale': this.language,
        },
      });
    }

    request = request.clone({
      setHeaders: {
        'ngsw-bypass': 'true',
      },
    });

    return next.handle(request).pipe(
      retryWhen(error =>
        error.pipe(
          concatMap((error, count) => {
            this.analyticsService.log({
              level: 'error',
              label: 'DoodApiInterceptor.RequestError',
              message: {
                url: request.url,
                message: error?.message || error?.toString(),
              },
            });

            if (!request.url.startsWith(DOOD_API)) {
              return throwError(() => error);
            }

            if (count <= retryCount && error.status === 0) {
              if (count >= 1) {
                this.noConnectionModalService.setDisplay(true);
              }
              this.noConnectionModalService.setRetryInProgress(true);
              return of(error).pipe(delay(this.calculateExponentialDelay(count)));
            }
            if (count > retryCount && error.status === 0) {
              this.noConnectionModalService.setRetryInProgress(false);
            }
            return throwError(() => error);
          }),
        ),
      ),
      tap(event => {
        if (event?.type === 0) {
          return;
        }
        this.noConnectionModalService.setDisplay(false);
        this.noConnectionModalService.setRetryInProgress(false);
      }),
    );
  }

  private getFirebaseToken(): void {
    this.authSelector.selectStatusToken
      .pipe(
        skipWhile(token => !token),
        tap(token => (this.firebaseToken = token)),
      )
      .subscribe();
  }
}
